import { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';
import { Note, Review } from '../types/items';
import { CareerCard } from '../types/careerCard';
import { hasContentChanged } from '../utils/changeDetection';
import { networkUtils } from '../utils/networkUtils';
import { useAuthState } from '../hooks/useAuthState';
import { User } from '@supabase/supabase-js';

const logger = createLogger({ module: 'useDraftRecovery' });

type DraftableItem = Note | Review | CareerCard;

interface Draft<T extends DraftableItem> {
  id: string;
  content: T;
  last_saved: string;
  original_version: T;
}

// Add this type guard
function hasUpdatedAt(item: unknown): item is { updated_at: string } {
  return typeof item === 'object' && item !== null && 'updated_at' in item;
}

// Add type to checkForDraft to handle mounted state
const checkForDraft = async (
  item: DraftableItem | null, 
  user: User | null,
  isMountedRef: React.MutableRefObject<boolean>,
  setDraft: React.Dispatch<React.SetStateAction<Draft<DraftableItem> | null>>,
  setShowRecoveryDialog: React.Dispatch<React.SetStateAction<boolean>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (!item?.id || !user) return;
  
  // Don't attempt to check for drafts if we're offline
  if (networkUtils.isOffline()) {
    if (isMountedRef.current) {
      setIsLoading(false);
    }
    return;
  }

  if (isMountedRef.current) {
    setIsLoading(true);
  }
  
  try {
    // Determine item type
    let itemType: 'note' | 'review' | 'career_card';
    if ('card_data' in item) {
      itemType = 'career_card';
    } else if ('review_text' in item) {
      itemType = 'review';
    } else {
      itemType = 'note';
    }
    
    const { data: drafts, error } = await supabase
      .from('drafts')
      .select('*')
      .eq('user_id', user.id)
      .eq('item_id', item.id)
      .eq('item_type', itemType)
      .in('status', ['draft', 'auto_saved'])
      .order('last_saved', { ascending: false })
      .limit(1);

    // Check if component is still mounted
    if (!isMountedRef.current) return;
    
    if (error) throw error;

    if (drafts && drafts.length > 0) {
      const draft = drafts[0];
      const draftContent = draft.content as unknown as DraftableItem;
      
      // Only show recovery dialog if:
      // 1. The draft is newer than the current version (if we can determine that)
      // 2. The content is actually different
      const isDraftNewer = !hasUpdatedAt(item) || new Date(draft.last_saved) > new Date(item.updated_at);
      const hasChanges = hasContentChanged(item, draftContent);

      logger.debug('Draft recovery check:', {
        isDraftNewer,
        hasChanges,
        draftLastSaved: draft.last_saved,
        itemLastUpdated: hasUpdatedAt(item) ? item.updated_at : 'unknown'
      });

      if (isDraftNewer && hasChanges) {
        if (isMountedRef.current) {
          setDraft(draft as Draft<DraftableItem>);
          setShowRecoveryDialog(true);
        }
      } else {
        // If the draft is older or identical, mark it as recovered
        // so it doesn't show up in future checks
        if (!networkUtils.isOffline()) {
          await supabase
            .from('drafts')
            .update({ status: 'recovered' })
            .eq('id', draft.id);
        }
      }
    }
  } catch (error) {
    if (isMountedRef.current) {
      logger.error('Error checking for drafts:', error);
    }
  } finally {
    if (isMountedRef.current) {
      setIsLoading(false);
    }
  }
};

// Update the hook implementation to use the extracted function
export function useDraftRecovery<T extends DraftableItem>(
  item: T | null,
  onRecover: (content: T) => void
) {
  const [isLoading, setIsLoading] = useState(false);
  const [draft, setDraft] = useState<Draft<T> | null>(null);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const isMountedRef = useRef(true);
  const { user, isAuthenticated } = useAuthState();

  // Use useAuthState to only check for drafts when authenticated
  useEffect(() => {
    if (!item?.id || !isAuthenticated || !user) return;
    
    // Use a wrapper function to pass the right type to checkForDraft
    const checkDrafts = async () => {
      await checkForDraft(
        item,
        user,
        isMountedRef, 
        setDraft as React.Dispatch<React.SetStateAction<Draft<DraftableItem> | null>>,
        setShowRecoveryDialog,
        setIsLoading
      );
    };

    checkDrafts();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [item?.id, isAuthenticated, user]); // Only depend on item ID and auth state

  const handleRecover = async () => {
    if (!draft) return;

    try {
      onRecover(draft.content as T);
      
      // Don't try to update the database if offline
      if (!networkUtils.isOffline()) {
        // Mark draft as recovered
        await supabase
          .from('drafts')
          .update({ status: 'recovered' })
          .eq('id', draft.id);
      }
    } catch (error) {
      logger.error('Error recovering draft:', error);
    } finally {
      setShowRecoveryDialog(false);
      setDraft(null);
    }
  };

  const handleDiscard = async () => {
    if (!draft) return;

    try {
      // Don't try to update the database if offline
      if (!networkUtils.isOffline()) {
        // Mark draft as discarded
        await supabase
          .from('drafts')
          .update({ status: 'discarded' })
          .eq('id', draft.id);
      }
    } catch (error) {
      logger.error('Error discarding draft:', error);
    } finally {
      setShowRecoveryDialog(false);
      setDraft(null);
    }
  };

  return {
    isLoading,
    draft,
    showRecoveryDialog,
    setShowRecoveryDialog,
    handleRecover,
    handleDiscard,
  };
} 